import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { storage } from "../firebase/config";

/**
 * Upload image to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} path - The storage path (e.g., 'profile-pictures')
 * @param {string} fileName - The filename for the image
 * @returns {Promise<string>} - Download URL of the uploaded image
 */
export const uploadImage = async (file, path, fileName) => {
  try {
    // Create a reference to the file location
    const storageRef = ref(storage, `${path}/${fileName}`);

    // Upload the file
    const snapshot = await uploadBytes(storageRef, file);

    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    throw new Error("Failed to upload image");
  }
};

/**
 * Delete image from Firebase Storage
 * @param {string} imageUrl - The full URL of the image to delete
 * @returns {Promise<void>}
 */
export const deleteImage = async (imageUrl) => {
  try {
    if (!imageUrl) return;

    // Extract the path from the URL
    const url = new URL(imageUrl);
    const path = url.pathname.split("/o/")[1]?.split("?")[0];

    if (path) {
      const decodedPath = decodeURIComponent(path);
      const imageRef = ref(storage, decodedPath);
      await deleteObject(imageRef);
    }
  } catch (error) {
    // Don't throw error for deletion failures
  }
};

/**
 * Generate unique filename for profile pictures
 * @param {string} email - User's email
 * @param {string} originalName - Original filename
 * @returns {string} - Unique filename
 */
export const generateProfileImageName = (email, originalName) => {
  const timestamp = Date.now();
  const extension = originalName.split(".").pop();
  const sanitizedEmail = email.replace(/[^a-zA-Z0-9]/g, "");
  return `profile_${sanitizedEmail}_${timestamp}.${extension}`;
};

/**
 * Validate image file
 * @param {File} file - The file to validate
 * @returns {Object} - Validation result with isValid and error message
 */
export const validateImage = (file) => {
  // Check file type
  if (!file.type.startsWith("image/")) {
    return { isValid: false, error: "Please select a valid image file" };
  }

  // Check file size (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    return { isValid: false, error: "Image size should be less than 5MB" };
  }

  return { isValid: true, error: null };
};
